import { useQuery } from '@tanstack/react-query';
import { LineChartIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useParams } from 'react-router';

import { Skeleton } from '@/components/core/skeleton';
import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { Page, PageContent } from '@/components/layout';
import { useGateways } from '@/gateways/GatewayProvider';
import { extractErrorMessage } from '@/shared/helpers';
import { useMessages } from '@/shared/providers/MessagesProvider';

import { useOmniTheme } from './use-omni-theme';

function Embed({ url, title }: { url?: string; title?: string }) {
  return url ? (
    <iframe
      src={url}
      className="-mx-2 h-full border-0"
      title={title ?? 'Insight'}
      allow="fullscreen"
    />
  ) : (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      There was an error loading this insight. Please try again.
    </div>
  );
}

export function InsightDetails() {
  const { id } = useParams<{ id: string }>();
  const { analyticsGateway } = useGateways();
  const messages = useMessages();
  const omniTheme = useOmniTheme();

  const { data, isLoading, error } = useQuery({
    queryKey: ['insights', id],
    queryFn: ({ signal }) => analyticsGateway.getInsight(id!, omniTheme, { signal }),
    enabled: !!id,
  });

  useEffect(() => {
    if (error) {
      messages.error(extractErrorMessage(error), { key: `insight-${id}-error` });
    }
  }, [id, error, messages]);

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/analytics/insights" parentTitle="Insights">
          <LineChartIcon />
        </HeaderIcon>
        <HeaderTitle>{data?.insight.name}</HeaderTitle>
      </Header>
      <PageContent variant="column">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <Embed url={data?.embedUrl} title={data?.insight.name} />
        )}
      </PageContent>
    </Page>
  );
}
